// src/components/JobTable.js
import React, { useEffect, useState, useCallback } from "react";
import { getData, postData } from "../../services/FetchNodeservices";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import toast, { Toaster } from "react-hot-toast";

/* =====================================
      Simple Tailwind Modal (Fixed JSX)
===================================== */
function Modal({ open, onClose, title, children, size = "md" }) {
  if (!open) return null;

  const sizes = {
    sm: "max-w-xl",
    md: "max-w-3xl",
    lg: "max-w-5xl",
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
      <div className="absolute inset-0" onClick={onClose}></div>

      <div
        className={`relative bg-white ${sizes[size]} w-full rounded-xl shadow-xl p-6`}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-black"
          >
            ✕
          </button>
        </div>

        {children}
      </div>
    </div>
  );
}

/* ===========================================================
     MAIN JOB TABLE COMPONENT
=========================================================== */

const DEFAULT_COLUMNS = [
  { label: "Requisition ID", key: "reqId" },
  { label: "Job Title", key: "title" },
  { label: "Company", key: "company" },
  { label: "Status", key: "status" },
  { label: "Posted Time", key: "created_at" },
  { label: "Candidates", key: "candidates" },
  { label: "Location", key: "location" },
  { label: "Assigned By", key: "assignedBy" },
  { label: "Assigned To", key: "assignedTo" },
];

export default function JobTable() {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState(DEFAULT_COLUMNS);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  /* ============================
        ASSIGN STATES
  ============================ */
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [employees, setEmployees] = useState([]);

  const [assignForm, setAssignForm] = useState({
    assigned_to: [],
    notes: "",
    target_count: "",
    assign_date: new Date().toISOString().split("T")[0],
  });

  
// ================= ROLE BASED ACCESS =================

const userId = Number(localStorage.getItem("user_id") || 0);
const [canAssign, setCanAssign] = useState(false);
  /* ===============================
        FETCH JOBS
  =============================== */
  const fetchJobs = useCallback(async () => {
    try {
      const res = await getData("job/jobs");
      const list = res?.jobs || res?.data || [];
      setRows(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("fetchJobs error:", err);
      setRows([]);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  /* ===============================
        FETCH EMPLOYEES (Assign)
  =============================== */
  useEffect(() => {
    async function loadEmployees() {
      try {
        if (!userId) return;
        const result = await getData("job/allowed-users/" + userId);
        if (result.status) {
          setEmployees(result.data || []);
          setCanAssign(result.canAssign || false);
        }
      } catch (err) {
        console.error(err);
      }
    }
    loadEmployees();
  }, [userId]);

  /* ===============================
        FETCH USER COLUMN SETTINGS
  =============================== */
  const fetchUserColumns = useCallback(async () => {
    try {
      const uid =
        localStorage.getItem("user_id") || localStorage.getItem("userId");
      if (!uid) return;

      const res = await getData(`job/preferences/${uid}`);
      if (res?.status && Array.isArray(res.data) && res.data.length > 0) {
        const mapped = res.data.map((key) => {
          const found = DEFAULT_COLUMNS.find((c) => c.key === key);
          return found || { label: key, key };
        });
        setColumns(mapped);
      }
    } catch (err) {
      console.error("fetchUserColumns:", err);
    }
  }, []);

  useEffect(() => {
    fetchUserColumns();
  }, [fetchUserColumns]);

  /* ===============================
        SEARCH FILTER
  =============================== */
  const filteredRows = rows.filter((r) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return Object.values(r).some(
      (v) => typeof v === "string" && v.toLowerCase().includes(s)
    );
  });

  const visibleRows = filteredRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const toggleSelect = (id) =>
    setSelected((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id]
    );

  const selectAll = (checked) => {
    if (checked) setSelected(filteredRows.map((r) => r.reqId));
    else setSelected([]);
  };

  /* ===============================
        EXPORT
  =============================== */
  const handleExport = () => {
    if (!selected.length) {
      alert("Select at least one row");
      return;
    }
    const dataToExport = rows.filter((r) => selected.includes(r.reqId));

    const headers = columns.map((c) => c.label);
    const mapped = dataToExport.map((r) => columns.map((c) => r[c.key]));

    const ws = XLSX.utils.aoa_to_sheet([headers, ...mapped]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Jobs");

    XLSX.writeFile(wb, `Jobs_${Date.now()}.xlsx`);
    setSelected([]);
  };

  /* ===============================
        STATUS CHANGE
  =============================== */
  const handleStatusChange = async (reqId, val) => {
    try {
      const res = await postData("job/update-status", {
        req_id: reqId,
        status: val,
      });
      if (res.status) fetchJobs();
    } catch (err) {
      console.error(err);
    }
  };

  /* ===============================
        ASSIGN CLICK
  =============================== */
  const handleAssignClick = (job) => {
    if (!canAssign) {
      toast.error("You are not allowed to assign jobs");
      return;
    }

    setSelectedJob(job);
    setAssignForm({
      assigned_to: [],
      notes: "",
      target_count: "",
      assign_date: new Date().toISOString().split("T")[0],
    });

    setAssignDialogOpen(true);
  };

  /* ===============================
        ASSIGN SUBMIT
  =============================== */
  const handleAssignSubmit = async () => {
    if (!selectedJob) {
      toast.error("No job selected");
      return;
    }

    if (assignForm.assigned_to.length === 0) {
      toast.error("Select at least one employee");
      return;
    }

    if (!assignForm.target_count || String(assignForm.target_count).trim() === "") {
      toast.error("Please add target count");
      return;
    }

    try {
      const selectedEmployeeIds = assignForm.assigned_to
        .map((empName) => {
          const emp = employees.find((e) => e.name === empName);
          return emp ? (emp.user_id || emp.id) : null;
        })
        .filter(Boolean);

      const body = {
        req_id: selectedJob.reqId,
        assigned_to: selectedEmployeeIds.join(","),
        assigned_by: userId,
        notes: assignForm.notes,
        target_count: assignForm.target_count,
        assign_date: assignForm.assign_date,
      };

      const res = await postData("job/assign", body);

      if (res.status) {
        toast.success("Job assigned successfully!");
        setAssignDialogOpen(false);
        setSelectedJob(null);

        setAssignForm({
          assigned_to: [],
          notes: "",
          target_count: "",
          assign_date: new Date().toISOString().split("T")[0],
        });

        fetchJobs();
      } else {
        toast.error(res.message || "Assignment failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
    }
  };

  /* ===============================
          UI RENDER
  =============================== */
  return (
    <div className="w-full space-y-6 bg-[#f4f6fa]/30 font-sans">
      <Toaster />

      {/* =================== HEADER =================== */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-gray-900 tracking-tight">
            Job Openings Table
          </h1>
          <p className="text-sm font-semibold text-gray-400 mt-1">
            Displaying {filteredRows.length} of {rows.length} jobs with custom columns and assignments
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            className="px-5 py-2.5 rounded-2xl bg-indigo-650 hover:bg-indigo-750 text-white font-extrabold shadow-lg shadow-indigo-100 hover:shadow-indigo-200 transition duration-200 text-xs bg-indigo-600"
            onClick={() => navigate("/dashboard/jobopening")}
          >
            Back to Cards
          </button>

          <button
            className="px-5 py-2.5 rounded-2xl bg-white border border-gray-200 text-gray-750 font-extrabold shadow-sm hover:bg-gray-50 transition duration-200 text-xs"
            onClick={() => navigate("/dashboard/customizetable")}
          >
            Customize Table
          </button>

          <button
            className="px-5 py-2.5 rounded-2xl bg-white border border-gray-200 text-gray-700 font-extrabold shadow-sm hover:bg-gray-50 transition duration-200 text-xs"
            onClick={handleExport}
          >
            Export Selected 📄
          </button>
        </div>
      </div>

      {/* SEARCH CARD */}
      <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-2xl shadow-indigo-100/20">
        <div className="max-w-md">
          <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-2">Search Jobs</label>
          <div className="relative">
            <span className="absolute inset-y-0 left-4 flex items-center pointer-events-none text-gray-400">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </span>
            <input
              type="text"
              placeholder="Search jobs by requisition ID, title, company..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              className="w-full pl-10 pr-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-medium"
            />
          </div>
        </div>
      </div>

      {/* =================== TABLE =================== */}
      <div className="bg-white border border-gray-100 rounded-3xl shadow-xl shadow-indigo-100/20 overflow-hidden">
        <div className="w-full overflow-auto" style={{ maxHeight: "70vh" }}>
          <table className="w-full border-collapse text-left text-sm text-gray-700">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50 text-[10px] font-bold text-gray-400 uppercase tracking-wider text-left">
                <th className="px-4 py-3.5 w-[50px]">
                  <input
                    type="checkbox"
                    className="rounded text-indigo-600 focus:ring-indigo-500"
                    checked={
                      selected.length > 0 &&
                      selected.length === filteredRows.length
                    }
                    onChange={(e) => selectAll(e.target.checked)}
                  />
                </th>

                {columns.map((col) => (
                  <th key={col.key} className="px-4 py-3.5">
                    {col.label}
                  </th>
                ))}

                <th className="px-4 py-3.5 w-[140px] text-right">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-50">
              {visibleRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length + 2}
                    className="px-6 py-12 text-center text-gray-400 font-semibold"
                  >
                    No job postings found matching criteria.
                  </td>
                </tr>
              ) : (
                visibleRows.map((row) => (
                  <tr
                    key={row.reqId}
                    className="hover:bg-gray-50/30 transition text-gray-700"
                  >
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        className="rounded text-indigo-600 focus:ring-indigo-500"
                        checked={selected.includes(row.reqId)}
                        onChange={() => toggleSelect(row.reqId)}
                      />
                    </td>

                    {columns.map((col) => (
                      <td key={col.key} className="px-4 py-4 text-xs font-medium">
                        {col.key === "assignedTo" ? (
                          row.assignedTo || row.assigned_to_names ? (
                            <div className="flex flex-wrap gap-1">
                              {(row.assignedTo || row.assigned_to_names)
                                .split(",")
                                .map((name, i) => (
                                  <span
                                    key={i}
                                    className="bg-indigo-50 border border-indigo-100 text-indigo-650 px-2 py-0.5 rounded-full text-[10px] font-bold"
                                  >
                                    {name.trim()}
                                  </span>
                                ))}
                            </div>
                          ) : (
                            "-"
                          )
                        ) : col.key === "assignedBy" ? (
                          row.assignedBy || row.assigned_by_name || "-"
                        ) : col.key === "status" ? (
                          <select
                            value={row.status || "Active"}
                            onChange={(e) =>
                              handleStatusChange(row.reqId, e.target.value)
                            }
                            className="bg-[#f4f6fa] border-none text-xs font-extrabold uppercase px-2.5 py-1 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-gray-700"
                          >
                            <option value="Active">Active</option>
                            <option value="Hold">Hold</option>
                            <option value="Closed">Closed</option>
                          </select>
                        ) : col.key === "candidates" ? (
                          <button
                            className="text-indigo-600 font-extrabold hover:underline"
                            onClick={() => navigate(`/jobcandidates/${row.reqId}`)}
                          >
                            {row.candidates ?? row.candidateCount ?? 0} Candidates
                          </button>
                        ) : col.key === "reqId" ? (
                          <span className="font-extrabold text-gray-500">#{row[col.key]}</span>
                        ) : col.key === "title" ? (
                          <span className="font-bold text-gray-900">{row[col.key]}</span>
                        ) : (
                          row[col.key] || "-"
                        )}
                      </td>
                    ))}

                    <td className="px-4 py-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          className="px-2.5 py-1 rounded-xl text-[11px] font-bold bg-[#f4f6fa]/60 text-gray-500 hover:bg-[#f4f6fa] hover:text-gray-700 transition"
                          onClick={() =>
                            navigate("/seejob", { state: row })
                          }
                        >
                          See
                        </button>

                        <button
                          className="px-2.5 py-1 rounded-xl text-[11px] font-bold bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition"
                          onClick={() => handleAssignClick(row)}
                        >
                          Assign
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* =================== PAGINATION =================== */}
        <div className="bg-gray-50/50 border-t border-gray-100 px-6 py-4 flex flex-col sm:flex-row justify-between sm:items-center gap-4">
          <div className="text-xs font-semibold text-gray-400">
            Page {page + 1} of {Math.ceil(filteredRows.length / rowsPerPage) || 1}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-gray-400 mr-1">Rows per page:</span>
            <select
              className="bg-white border border-gray-200 text-xs font-bold px-2 py-1 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-gray-700"
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(0);
              }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
            </select>

            <button
              className="px-3 py-1 rounded-xl text-xs font-bold border border-gray-200 bg-white hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Prev
            </button>

            <button
              className="px-3 py-1 rounded-xl text-xs font-bold border border-gray-200 bg-white hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={(page + 1) * rowsPerPage >= filteredRows.length}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* =================== ASSIGN MODAL =================== */}
      {assignDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setAssignDialogOpen(false)} />
          <div className="relative z-10 w-full max-w-md bg-white rounded-[32px] shadow-2xl p-8 border border-gray-100 overflow-hidden animate-fadeIn">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-lg font-black text-gray-900">Assign Job</h3>
                {selectedJob && <p className="text-[10px] text-gray-400 mt-0.5">{selectedJob.title}</p>}
              </div>
              <button 
                onClick={() => setAssignDialogOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* Employee List */}
              <div>
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-2">Assign to Employees</label>

                <div className="border border-gray-100 bg-[#f4f6fa]/30 rounded-2xl p-3 max-h-48 overflow-y-auto space-y-1.5 no-scrollbar">
                  {employees.map((emp) => (
                    <label key={emp.id} className="flex items-center gap-2.5 py-1 text-xs font-semibold text-gray-700 cursor-pointer hover:text-gray-900">
                      <input
                        type="checkbox"
                        className="rounded text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5"
                        checked={assignForm.assigned_to.includes(emp.name)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setAssignForm((prev) => ({
                              ...prev,
                              assigned_to: [...prev.assigned_to, emp.name],
                            }));
                          } else {
                            setAssignForm((prev) => ({
                              ...prev,
                              assigned_to: prev.assigned_to.filter(
                                (n) => n !== emp.name
                              ),
                            }));
                          }
                        }}
                      />
                      {emp.name}
                    </label>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">Assignment Notes</label>
                <textarea
                  placeholder="Enter details, requests, or constraints..."
                  className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-semibold h-20"
                  value={assignForm.notes}
                  onChange={(e) =>
                    setAssignForm((p) => ({ ...p, notes: e.target.value }))
                  }
                />
              </div>

              {/* Target Count & Date */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">Target Count</label>
                  <input
                    type="number"
                    className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-semibold"
                    value={assignForm.target_count}
                    onChange={(e) =>
                      setAssignForm((p) => ({
                        ...p,
                        target_count: e.target.value,
                      }))
                    }
                  />
                </div>

                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">Assign Date</label>
                  <input
                    type="date"
                    className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-semibold"
                    value={assignForm.assign_date}
                    onChange={(e) =>
                      setAssignForm((p) => ({
                        ...p,
                        assign_date: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  className="bg-[#f4f6fa] text-gray-700 font-bold px-5 py-2.5 rounded-2xl hover:bg-gray-100 transition text-xs"
                  onClick={() => setAssignDialogOpen(false)}
                >
                  Cancel
                </button>

                <button
                  className={`px-5 py-2.5 rounded-2xl font-bold transition text-xs ${
                    assignForm.assigned_to.length === 0
                      ? "bg-gray-150 text-gray-400 cursor-not-allowed bg-gray-100"
                      : "bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-100"
                  }`}
                  disabled={assignForm.assigned_to.length === 0}
                  onClick={handleAssignSubmit}
                >
                  Assign Job
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
